<template>
  <div class="quick-link-manager">
    <div class="panel-row">
      <!-- 左侧：添加快捷入口（三种类型） -->
      <div class="add-panel">
        <div class="panel-title">添加快捷入口</div>

        <!-- 类型切换 Tab -->
        <div class="type-tabs">
          <button :class="['type-tab', { active: activeType === 'module' }]" @click="switchType('module')">
            <i class="fas fa-th-large"></i> 从模块
          </button>
          <button :class="['type-tab', { active: activeType === 'process' }]" @click="switchType('process')">
            <i class="fas fa-sitemap"></i> 从流程
          </button>
          <button :class="['type-tab', { active: activeType === 'custom' }]" @click="switchType('custom')">
            <i class="fas fa-pen"></i> 自定义
          </button>
        </div>

        <!-- 从模块添加 -->
        <div v-if="activeType === 'module'" class="type-content">
          <div v-if="modules.length === 0" class="empty-msg">
            <i class="fas fa-info-circle"></i> 暂无可用的模块
          </div>
          <div v-else class="module-grid">
            <div
              v-for="m in modules"
              :key="m.guid"
              class="module-card"
              :class="{ added: addedModuleIds.has(m.guid) }"
              @click="addModuleLink(m)"
            >
              <i :class="m.iconCl || m.icon || 'fas fa-link'"></i>
              <span class="module-name">{{ m.name || m.enName }}</span>
              <span v-if="addedModuleIds.has(m.guid)" class="added-badge"><i class="fas fa-check"></i> 已添加</span>
            </div>
          </div>
        </div>

        <!-- 从流程添加 -->
        <div v-if="activeType === 'process'" class="type-content">
          <div class="form-group">
            <input
              v-model="processSearchText"
              type="text"
              placeholder="搜索流程..."
              class="form-input"
              @input="filterProcesses"
            />
          </div>
          <div v-if="filteredProcesses.length === 0" class="empty-msg">
            <i class="fas fa-info-circle"></i> {{ processSearchText ? '无匹配流程' : '暂无可用的流程' }}
          </div>
          <div v-else class="process-grid">
            <div
              v-for="p in filteredProcesses"
              :key="p.guid"
              class="process-card"
              :class="{ added: addedProcessIds.has(p.guid) }"
              @click="addProcessLink(p)"
            >
              <i class="fas fa-file-alt"></i>
              <span class="process-name">{{ p.processName }}</span>
              <span v-if="addedProcessIds.has(p.guid)" class="added-badge"><i class="fas fa-check"></i> 已添加</span>
            </div>
          </div>
        </div>

        <!-- 自定义添加 -->
        <div v-if="activeType === 'custom'" class="type-content">
          <div class="form-group">
            <label><i class="fas fa-tag"></i> 名称 <span class="required">*</span></label>
            <input v-model="customForm.name" type="text" placeholder="输入快捷入口名称" class="form-input" />
          </div>
          <div class="form-group">
            <label><i class="fas fa-link"></i> 路径 <span class="required">*</span></label>
            <input v-model="customForm.url" type="text" placeholder="输入跳转路径，如 /system/management" class="form-input" />
            <p class="field-hint">支持系统内路径或外部链接（http/https 开头）</p>
          </div>
          <div class="form-group">
            <label><i class="fas fa-icons"></i> 图标</label>
            <div class="icon-picker-row">
              <button class="icon-pick-btn" @click="showIconPicker = true">
                <i :class="customForm.icon || 'fas fa-icons'"></i>
                <span>{{ customForm.icon ? '已选择' : '选择图标' }}</span>
              </button>
            </div>
          </div>
          <button class="btn btn-primary add-btn" :disabled="!customForm.name || !customForm.url" @click="addCustomLink">
            <i class="fas fa-plus"></i> 添加自定义入口
          </button>
        </div>
      </div>

      <!-- 右侧：我的快捷入口 -->
      <div class="my-panel">
        <div class="panel-title">
          我的快捷入口
          <span class="count-badge">{{ myLinks.length }}</span>
        </div>

        <div v-if="myLinks.length === 0" class="empty-msg">
          <i class="fas fa-bookmark"></i> 还没有添加快捷入口
        </div>
        <div v-else class="link-list">
          <div v-for="(item, idx) in myLinks" :key="item.guid" class="link-item">
            <div class="link-info">
              <i :class="(item.icon || 'fas fa-link') + ' link-icon'"></i>
              <div class="link-detail">
                <span class="link-name">{{ item.title }}</span>
                <span class="link-meta">
                  <span class="link-type-tag">{{ item.para0 === 'process' ? '流程' : item.para0 === 'module' ? '模块' : '自定义' }}</span>
                  <template v-if="item.isDefault">
                    <span class="sys-tag">系统</span>
                  </template>
                </span>
              </div>
            </div>
            <div class="link-actions">
              <span class="order-num">#{{ idx + 1 }}</span>
              <button
                v-if="!item.isDefault"
                class="btn-icon btn-danger"
                title="删除"
                @click="deleteLink(item)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 图标选择器弹窗 -->
    <div v-if="showIconPicker" class="modal-overlay" @click.self="showIconPicker = false">
      <div class="icon-picker-modal">
        <div class="modal-header">
          <span>选择图标</span>
          <button class="btn-close" @click="showIconPicker = false">&times;</button>
        </div>
        <div class="icon-search">
          <input v-model="iconSearch" type="text" placeholder="搜索图标..." class="form-input" />
        </div>
        <div class="icon-grid">
          <button
            v-for="ic in filteredIcons"
            :key="ic"
            :class="['icon-option', { selected: customForm.icon === ic }]"
            @click="customForm.icon = ic; showIconPicker = false"
          >
            <i :class="ic"></i>
            <span>{{ ic.split('fa-').pop() }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getMyQuickLinks, getAvailableModules, getAvailableProcesses, createQuickLink, deleteQuickLink } from '../../api/quicklink'

const activeType = ref('module')

// ==================== 从模块添加 ====================
const modules = ref([])
const myLinks = ref([])

const addedModuleIds = computed(() => {
  const ids = new Set()
  myLinks.value.forEach(l => {
    if (l.para0 === 'module' && l.para1) ids.add(l.para1)
  })
  return ids
})

async function loadData() {
  try {
    const [modRes, mineRes] = await Promise.all([
      getAvailableModules(),
      getMyQuickLinks()
    ])
    if (modRes.resultType) modules.value = modRes.data
    if (mineRes.resultType) myLinks.value = mineRes.data
  } catch (e) {
    $message.error('加载数据失败')
  }
}

async function addModuleLink(m) {
  if (addedModuleIds.value.has(m.guid)) {
    $message.warning('已添加过该模块')
    return
  }
  try {
    const res = await createQuickLink({
      Title: m.name || m.enName,
      Icon: m.iconCl || m.icon || 'fas fa-link',
      Path: m.url || '',
      Para0: 'module',
      Para1: m.guid
    })
    if (res.resultType) {
      $message.success('添加成功')
      loadData()
    } else {
      $message.error(res.message || '添加失败')
    }
  } catch (e) {
    $message.error('添加失败: ' + (e.response?.data?.Message || e.message))
  }
}

// ==================== 从流程添加 ====================
const processes = ref([])
const processSearchText = ref('')

const addedProcessIds = computed(() => {
  const ids = new Set()
  myLinks.value.forEach(l => {
    if (l.para0 === 'process' && l.para1) ids.add(l.para1)
  })
  return ids
})

const filteredProcesses = computed(() => {
  if (!processSearchText.value) return processes.value
  const kw = processSearchText.value.toLowerCase()
  return processes.value.filter(p => (p.processName || '').toLowerCase().includes(kw))
})

async function loadProcesses() {
  try {
    const res = await getAvailableProcesses()
    if (res.resultType) {
      processes.value = res.data
    } else {
      $message.error(res.message || '加载流程列表失败')
    }
  } catch (e) {
    console.error('加载流程列表异常:', e)
    $message.error('加载流程列表失败')
  }
}

function filterProcesses() {
  // 搜索时清空选中状态（保留为后续扩展）
}

async function addProcessLink(p) {
  if (addedProcessIds.value.has(p.guid)) {
    $message.warning('已添加过该流程')
    return
  }
  try {
    const res = await createQuickLink({
      Title: p.processName,
      Icon: 'fas fa-file-alt',
      Path: 'launchableProcesses',
      Para0: 'process',
      Para1: p.guid
    })
    if (res.resultType) {
      $message.success('添加成功')
      processSearchText.value = ''
      loadData()
    } else {
      $message.error(res.message || '添加失败')
    }
  } catch (e) {
    $message.error('添加失败: ' + (e.response?.data?.Message || e.message))
  }
}

// ==================== 自定义添加 ====================
const customForm = ref({ name: '', url: '', icon: '' })

async function addCustomLink() {
  if (!customForm.value.name || !customForm.value.url) return
  try {
    const res = await createQuickLink({
      Title: customForm.value.name,
      Icon: customForm.value.icon || 'fas fa-link',
      Path: customForm.value.url,
      Para0: 'custom',
      Para1: ''
    })
    if (res.resultType) {
      $message.success('添加成功')
      customForm.value = { name: '', url: '', icon: '' }
      loadData()
    } else {
      $message.error(res.message || '添加失败')
    }
  } catch (e) {
    $message.error('添加失败: ' + (e.response?.data?.Message || e.message))
  }
}

// ==================== 删除 ====================
async function deleteLink(item) {
  try {
    const res = await deleteQuickLink(item.guid)
    if (res.resultType) {
      $message.success('删除成功')
      loadData()
    } else {
      $message.error(res.message || '删除失败')
    }
  } catch (e) {
    $message.error('删除失败: ' + (e.response?.data?.Message || e.message))
  }
}

// ==================== 图标选择器 ====================
const showIconPicker = ref(false)
const iconSearch = ref('')

const iconList = [
  'fas fa-home', 'fas fa-user', 'fas fa-cog', 'fas fa-chart-bar', 'fas fa-file-alt',
  'fas fa-tasks', 'fas fa-envelope', 'fas fa-bell', 'fas fa-calendar', 'fas fa-search',
  'fas fa-sitemap', 'fas fa-database', 'fas fa-code', 'fas fa-shield-alt', 'fas fa-key',
  'fas fa-users', 'fas fa-tag', 'fas fa-bookmark', 'fas fa-star', 'fas fa-heart',
  'fas fa-th', 'fas fa-list', 'fas fa-table', 'fas fa-chart-pie', 'fas fa-chart-line',
  'fas fa-wrench', 'fas fa-tools', 'fas fa-edit', 'fas fa-trash', 'fas fa-plus',
  'fas fa-check', 'fas fa-times', 'fas fa-exclamation', 'fas fa-info-circle',
  'fas fa-download', 'fas fa-upload', 'fas fa-sync', 'fas fa-layer-group',
  'fas fa-clipboard-list', 'fas fa-clipboard', 'fas fa-file-invoice', 'fas fa-newspaper',
  'fas fa-paper-plane', 'fas fa-comments', 'fas fa-building', 'fas fa-briefcase',
  'fas fa-graduation-cap', 'fas fa-lock', 'fas fa-unlock', 'fas fa-history',
  'fas fa-sign-out-alt', 'fas fa-sign-in-alt', 'fas fa-gem', 'fas fa-crown',
  'fas fa-rocket', 'fas fa-map', 'fas fa-globe', 'fas fa-filter', 'fas fa-print',
  'fas fa-ban', 'fas fa-archive', 'fas fa-folder', 'fas fa-folder-open'
]

const filteredIcons = computed(() => {
  if (!iconSearch.value) return iconList
  const kw = iconSearch.value.toLowerCase()
  return iconList.filter(i => i.includes(kw))
})

// ==================== Tab 切换 ====================
function switchType(type) {
  activeType.value = type
  if (type === 'process') loadProcesses()
}

// ==================== 初始化 ====================
onMounted(loadData)
</script>

<style scoped>
.quick-link-manager {
  padding: 0;
}

.panel-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.add-panel {
  flex: 1 1 480px;
  min-width: 400px;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  padding: 16px;
}

.my-panel {
  flex: 0 0 320px;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  padding: 16px;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  background: #e5e7eb;
  color: #6b7280;
  font-size: 11px;
  border-radius: 10px;
  padding: 1px 8px;
}

/* ==================== Type Tabs ==================== */
.type-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 3px;
}

.type-tab {
  flex: 1;
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all .15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.type-tab.active {
  background: #fff;
  color: #1f2937;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}

.type-tab:hover:not(.active) {
  color: #374151;
}

.type-content {
  max-height: 420px;
  overflow-y: auto;
}

/* ==================== Module Grid ==================== */
.module-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.module-card {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all .15s;
  position: relative;
  font-size: 12px;
  color: #374151;
}

.module-card:hover {
  border-color: #10b981;
  background: #f0fdf4;
}

.module-card.added {
  border-color: #d1d5db;
  background: #f9fafb;
  color: #9ca3af;
  cursor: default;
}

.module-card i {
  font-size: 14px;
  color: #6b7280;
  flex-shrink: 0;
}

.module-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.added-badge {
  font-size: 10px;
  color: #10b981;
  flex-shrink: 0;
  margin-left: auto;
}

/* ==================== Process Grid ==================== */
.process-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 8px;
}

.process-card {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all .15s;
  position: relative;
  font-size: 12px;
  color: #374151;
}

.process-card:hover {
  border-color: #10b981;
  background: #f0fdf4;
}

.process-card.added {
  border-color: #d1d5db;
  background: #f9fafb;
  color: #9ca3af;
  cursor: default;
}

.process-card i {
  font-size: 14px;
  color: #6b7280;
  flex-shrink: 0;
}

.process-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ==================== Form ==================== */
.form-group {
  margin-bottom: 12px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
  transition: border-color .15s;
}

.form-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16,185,129,.1);
}

.field-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 3px;
}

.add-btn {
  width: 100%;
  margin-top: 4px;
}

/* ==================== My Links ==================== */
.empty-msg {
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.empty-msg i {
  font-size: 24px;
}

.link-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.link-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: border-color .15s;
}

.link-item:hover {
  border-color: #d1d5db;
}

.link-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.link-icon {
  font-size: 14px;
  color: #6b7280;
  flex-shrink: 0;
}

.link-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.link-name {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-meta {
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
}

.link-type-tag {
  background: #e5e7eb;
  padding: 0 5px;
  border-radius: 3px;
  font-size: 10px;
}

.sys-tag {
  background: #dbeafe;
  color: #2563eb;
  padding: 0 5px;
  border-radius: 3px;
  font-size: 10px;
}

.link-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.order-num {
  font-size: 11px;
  color: #9ca3af;
}

.btn-icon {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .15s;
}

.btn-danger {
  background: #fef2f2;
  color: #dc2626;
}

.btn-danger:hover {
  background: #dc2626;
  color: #fff;
}

/* ==================== Icon Picker ==================== */
.icon-picker-row {
  display: flex;
  gap: 8px;
}

.icon-pick-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
  transition: border-color .15s;
}

.icon-pick-btn:hover {
  border-color: #10b981;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.3);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-picker-modal {
  width: 480px;
  max-height: 480px;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 600;
}

.btn-close {
  border: none;
  background: none;
  font-size: 18px;
  cursor: pointer;
  color: #9ca3af;
}

.icon-search {
  padding: 10px 16px;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  padding: 0 16px 16px;
  overflow-y: auto;
}

.icon-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 4px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all .15s;
}

.icon-option i {
  font-size: 16px;
  color: #6b7280;
}

.icon-option span {
  font-size: 9px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.icon-option:hover {
  border-color: #10b981;
  background: #f0fdf4;
}

.icon-option.selected {
  border-color: #10b981;
  background: #ecfdf5;
}
</style>
